/**
 * Shared transfer-out / PayPal payout helpers (consumer profile + preferences payloads).
 * Keeps the same rules as Profile payouts: verified unless explicitly false; email required.
 */

export type TransferOutPaypalFields = {
  paypalEmail?: string | null;
  paypalEmailVerified?: boolean | null;
};

export type TransferOutChannelPreferences = Record<
  string,
  { enabled?: boolean } | undefined
>;

export type TransferOutPreferencePayload = TransferOutPaypalFields & {
  transferOutPreferences?: TransferOutChannelPreferences;
  /** Present when GET /profile includes a connected Express/Connect account id. */
  stripeConnectId?: string | null;
};

export type ProfileTransferOutShape = {
  profile?: TransferOutPaypalFields & {
    transferOutPreferences?: TransferOutChannelPreferences;
    stripeConnectId?: string | null;
  };
};

/** Matches ProfilePage: no email → not ready; verified flag false → not ready; legacy undefined → ready if email present. */
export function isPayPalPayoutDestinationReady(
  fields: TransferOutPaypalFields | null | undefined
): boolean {
  if (fields == null || typeof fields !== 'object') return false;
  const email = (fields.paypalEmail ?? '').trim();
  if (!email) return false;
  if (fields.paypalEmailVerified === false) return false;
  return true;
}

/**
 * PayPal channel is off only when explicitly disabled.
 * Undefined/missing → treat as enabled for legacy accounts that predate toggles.
 */
export function isPaypalTransferOutChannelEnabled(
  payload: Pick<TransferOutPreferencePayload, 'transferOutPreferences'> | null | undefined
): boolean {
  return payload?.transferOutPreferences?.paypal?.enabled !== false;
}

/** Bank / ACH transfer-out: off only when explicitly disabled (matches PayPal legacy behavior). */
export function isBankTransferOutChannelEnabled(
  payload: Pick<TransferOutPreferencePayload, 'transferOutPreferences'> | null | undefined
): boolean {
  return payload?.transferOutPreferences?.bank?.enabled !== false;
}

/**
 * Stripe Connect payout: requires a connected account id; off when `stripe.enabled === false`.
 */
export function isStripeConnectTransferOutAvailable(
  payload: Pick<TransferOutPreferencePayload, 'transferOutPreferences' | 'stripeConnectId'> | null | undefined
): boolean {
  if (payload?.transferOutPreferences?.stripe?.enabled === false) return false;
  const id = (payload?.stripeConnectId ?? '').trim();
  return id.length > 0;
}

/**
 * Merge GET /profile/preferences/transfer-out with GET /profile so PayPal email + verification
 * stay consistent with the payouts UI (preferences alone may omit profile-only fields).
 */
export function mergeTransferOutPreferencePayloadWithProfile(
  preferencePayload: TransferOutPreferencePayload | null | undefined,
  profileResponse: ProfileTransferOutShape | null | undefined
): TransferOutPreferencePayload {
  const prefs: TransferOutPreferencePayload =
    preferencePayload && typeof preferencePayload === 'object' ? { ...preferencePayload } : {};
  const nested = profileResponse?.profile;

  const prefEmail = (prefs.paypalEmail ?? '').trim();
  const profileEmail = (nested?.paypalEmail ?? '').trim();
  prefs.paypalEmail = prefEmail || profileEmail || undefined;

  prefs.paypalEmailVerified =
    prefs.paypalEmailVerified !== undefined && prefs.paypalEmailVerified !== null
      ? prefs.paypalEmailVerified
      : nested?.paypalEmailVerified;

  const fromPref = prefs.transferOutPreferences;
  const fromProfile = nested?.transferOutPreferences;
  if (fromPref || fromProfile) {
    prefs.transferOutPreferences = { ...fromProfile, ...fromPref };
  }

  const prefStripe = (prefs.stripeConnectId ?? '').trim();
  const profileStripe = (nested?.stripeConnectId ?? '').trim();
  if (prefStripe || profileStripe) {
    prefs.stripeConnectId = prefStripe || profileStripe;
  }

  return prefs;
}
